import { Component, Input } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import {
  DraftIncomeSource,
  WorkNumberDetail,
  WorkNumberAnnualComp,
  parseDraftDetail,
} from 'src/app/models/income-wizard-workflow';

@Component({
  selector: 'app-work-number-wizard-detail',
  templateUrl: './work-number-wizard-detail.component.html',
  styleUrls: ['./work-number-wizard-detail.component.less'],
  animations: [
    trigger('cardEnter', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(12px)' }),
        animate('280ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
    trigger('slideDown', [
      transition(':enter', [
        style({ opacity: 0, height: 0, overflow: 'hidden' }),
        animate('200ms ease-out', style({ opacity: 1, height: '*' })),
      ]),
      transition(':leave', [
        style({ opacity: 1, height: '*', overflow: 'hidden' }),
        animate('160ms ease-in', style({ opacity: 0, height: 0 })),
      ]),
    ]),
  ],
})
export class WorkNumberWizardDetailComponent {
  @Input() applicantName = '';
  @Input() existingDraft: DraftIncomeSource | null = null;

  showMore = false;

  get detail(): WorkNumberDetail | null {
    return parseDraftDetail<WorkNumberDetail>(this.existingDraft);
  }

  get employerName(): string | null {
    return this.detail?.employerName ?? this.existingDraft?.employerName ?? null;
  }

  get employmentStatus(): string | null {
    return this.detail?.employmentStatus ?? null;
  }

  get statusBadgeClass(): string {
    const s = (this.employmentStatus ?? '').toLowerCase();
    if (s.includes('active') || s.includes('current')) return 'wn-badge-active';
    if (s.includes('inactive') || s.includes('terminated') || s.includes('separated')) return 'wn-badge-inactive';
    if (s.includes('leave')) return 'wn-badge-leave';
    return 'wn-badge-unknown';
  }

  get streamPersonName(): string {
    return [this.detail?.firstName, this.detail?.lastName].filter(Boolean).join(' ') || '—';
  }

  get nameMatches(): boolean {
    if (!this.applicantName || !this.detail?.firstName) return false;
    const vendor = this.streamPersonName.toLowerCase().trim();
    const applicant = this.applicantName.toLowerCase().trim();
    return vendor === applicant;
  }

  get suggestedMonthly(): number | null {
    const amt = this.existingDraft?.suggestedAmount;
    return amt != null && amt > 0 ? amt : null;
  }

  get projectedAnnual(): number | null {
    return this.detail?.projectedAnnualIncome ?? null;
  }

  get annualCompRows(): WorkNumberAnnualComp[] {
    return this.detail?.annualCompHistory ?? [];
  }

  get hasMoreDetail(): boolean {
    return (
      this.detail?.rateOfPay != null ||
      this.detail?.payFrequency != null ||
      this.annualCompRows.length > 0
    );
  }

  get failedSopChecks() {
    return this.detail?.sopChecks?.filter(c => !c.passed) ?? [];
  }
}
